import { dollars } from './buildings';
import { minuteOfDay } from './clock';
import { TICKS_PER_GAME_HOUR, TICKS_PER_GAME_MINUTE } from './constants';

/*
 * Runway pavement. Every touchdown leaves rubber on the runway and wears its surface (wear in
 * per mille, per runway). A worn runway sheds debris: now and then a tyre is cut on landing
 * (FOD), the aircraft needs a tyre change before its next flight, and the airport pays the
 * airline's claim. Resurfacing (rubber removal and repairs) closes the runway for a few hours
 * and wears it back to new; it can start at once or tonight at 01:00, when traffic is light.
 */

export const WEAR_MAX = 1000;
/** Wear per touchdown by aircraft class (index = AircraftClassId). */
export const WEAR_PER_LANDING: readonly number[] = [0, 1, 1, 2, 1, 1, 1, 1, 1, 0, 3, 3];
/** From here on the runway is worn (some debris), and poor (a lot of it). */
export const WEAR_WORN = 600;
export const WEAR_POOR = 850;
/** Chance (per mille) per landing that debris cuts a tyre, on a worn and on a poor runway. */
export const FOD_PERMILLE_WORN = 4;
export const FOD_PERMILLE_POOR = 20;
/** Tyre change before the next flight (minutes), and the airline's claim the airport pays. */
export const FOD_REPAIR_MINUTES = 40;
export const FOD_CLAIM = dollars(6_000);
/** Below this wear there is nothing worth resurfacing. */
export const RESURFACE_MIN_WEAR = 50;
/** How long resurfacing closes the runway, and what it costs by runway level. */
export const RESURFACE_HOURS = 5;
export const RESURFACE_COST: readonly number[] = [
  0,
  dollars(15_000),
  dollars(30_000),
  dollars(50_000),
];
/** "Tonight" means from 01:00 (minute of the day). */
export const RESURFACE_NIGHT_MINUTE = 60;

/** Why a runway is closed. Saved: append only. */
export const ClosureKind = { Emergency: 0, Works: 1, Scramble: 2, Snow: 3, Flyover: 4 } as const;
export type ClosureKindId = (typeof ClosureKind)[keyof typeof ClosureKind];

/** Chance (per mille) that a landing on a runway this worn cuts a tyre. */
export function fodPermille(wear: number): number {
  if (wear >= WEAR_POOR) return FOD_PERMILLE_POOR;
  return wear >= WEAR_WORN ? FOD_PERMILLE_WORN : 0;
}

export function resurfaceCost(level: number): number {
  return RESURFACE_COST[Math.min(level, RESURFACE_COST.length - 1)] ?? 0;
}

/**
 * The crews need this long to set up (minutes), which lets aircraft already on approach or
 * taxiing out use the runway first.
 */
export const WORKS_LEAD_MINUTES = 15;

/** When resurfacing ordered at `time` starts: soon, or at the next 01:00 (at least the lead away). */
export function worksStart(time: number, tonight: boolean): number {
  const lead = WORKS_LEAD_MINUTES * TICKS_PER_GAME_MINUTE;
  if (!tonight) return time + lead;
  let ahead = (RESURFACE_NIGHT_MINUTE - minuteOfDay(time) + 1440) % 1440;
  if (ahead < WORKS_LEAD_MINUTES) ahead += 1440;
  return time - (time % TICKS_PER_GAME_MINUTE) + ahead * TICKS_PER_GAME_MINUTE;
}

export const RESURFACE_TICKS = RESURFACE_HOURS * TICKS_PER_GAME_HOUR;
